import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { listNewsletterCampaigns, deleteNewsletterCampaign } from "@/lib/api/newsletter.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { NewsletterAdminNav } from "@/components/admin/NewsletterAdminNav";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/newsletter/newsletters/")({
  loader: () => listNewsletterCampaigns(),
  component: NewslettersListPage,
});

type Row = Awaited<ReturnType<typeof listNewsletterCampaigns>>[number];

function buildColumns(onDelete: (row: Row) => Promise<void>): ColumnDef<Row>[] {
  return [
    {
      accessorKey: "subject",
      header: "Subject",
      cell: ({ row }) => <span className="font-medium">{row.original.subject}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: "sentCount",
      header: "Reached",
      cell: ({ row }) => {
        const { sentCount, totalRecipients, status } = row.original;
        if (status === "draft") return "—";
        return `${sentCount.toLocaleString()} / ${totalRecipients.toLocaleString()}`;
      },
    },
    {
      accessorKey: "failedCount",
      header: "Failed",
      cell: ({ row }) => (row.original.failedCount ? row.original.failedCount.toLocaleString() : "0"),
    },
    {
      accessorKey: "sentAt",
      header: "Sent",
      cell: ({ row }) => (row.original.sentAt ? new Date(row.original.sentAt).toLocaleString() : "—"),
    },
    {
      ...tableActionsColumn<Row>(),
      cell: ({ row }) => (
        <TableRowActions
          edit={{ to: "/admin/newsletter/newsletters/$id", params: { id: row.original.id } }}
          onDelete={row.original.status === "draft" ? () => onDelete(row.original) : undefined}
          showDelete={row.original.status === "draft"}
          deleteLabel={row.original.subject}
        />
      ),
    },
  ];
}

function NewslettersListPage() {
  const data = Route.useLoaderData();
  const router = useRouter();

  async function handleDelete(row: Row) {
    try {
      await deleteNewsletterCampaign({ data: { id: row.id } });
      toast.success("Newsletter deleted");
      router.invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
      throw e;
    }
  }

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Newsletter — Campaigns"
        action={
          <Button asChild>
            <Link to="/admin/newsletter/newsletters/new">
              <Plus className="mr-2 h-4 w-4" /> New newsletter
            </Link>
          </Button>
        }
      />
      <NewsletterAdminNav />
      <DataTable tableId="newsletters" columns={buildColumns(handleDelete)} data={data} />
    </AdminPageShell>
  );
}
